import { Button, SelectInput, SelectInputItem } from '@ads/components-react';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { EClassRoomDisciplinesStatus } from '@/shared/api/generated';
import { DatePicker } from '@/shared/ui/atoms';

import { useClassPlanningFilters } from '../../../hooks/useClassPlanningFilters';
import {
  CLASS_PLANNING_STATUS_LABEL_KEY,
  CLASS_PLANNING_STATUS_VALUES,
} from '../../../utils/classPlanningStatus';
import {
  ClassModuleAutocomplete,
  ClassStudentAutocomplete,
  CompanyAutocomplete,
  CourseAutocomplete,
  TeacherAutocomplete,
} from '../LookupAutocomplete';

/**
 * Barra de filtros da listagem de planejamento de aulas.
 *
 * Mantém o estado pendente localmente e só aplica ao acionar "Filtrar"
 * (`updateFilters`), incluindo o filtro de Status (multi-seleção via `TagSelect`)
 * — uma única regra de aplicação para todos os campos. "Limpar" zera todos os
 * campos e o estado aplicado (`resetFilters`). Trocar o Curso limpa o Módulo
 * selecionado, já que a busca de módulos é escopada pelo curso.
 */
export function ClassPlanningFilterBar() {
  const { t } = useTranslation('classPlanning');
  const { updateFilters, resetFilters } = useClassPlanningFilters();

  const [companyId, setCompanyId] = useState<number | null>(null);
  const [companyLabel, setCompanyLabel] = useState('');
  const [courseId, setCourseId] = useState<number | null>(null);
  const [courseLabel, setCourseLabel] = useState('');
  const [classStudentId, setClassStudentId] = useState<number | null>(null);
  const [classStudentLabel, setClassStudentLabel] = useState('');
  const [classModuleId, setClassModuleId] = useState<number | null>(null);
  const [classModuleLabel, setClassModuleLabel] = useState('');
  const [teacherId, setTeacherId] = useState<number | null>(null);
  const [teacherName, setTeacherName] = useState('');
  const [statusValue, setStatusValue] = useState('all');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  // Incrementado ao limpar para remontar os autocompletes — o `Autocomplete` do
  // ADS mantém o texto exibido em estado interno e só o reinicializa ao remontar.
  const [resetNonce, setResetNonce] = useState(0);

  const handleCourseChange = (id: number | null, label: string) => {
    setCourseId(id);
    setCourseLabel(label);
    // O lookup de módulo é escopado pelo curso — um módulo já selecionado pode
    // não pertencer mais ao novo curso.
    setClassModuleId(null);
    setClassModuleLabel('');
  };

  const handleSearch = () => {
    updateFilters({
      companyId,
      companyLabel,
      courseId,
      courseLabel,
      classStudentId,
      classStudentLabel,
      classModuleId,
      classModuleLabel,
      status: statusValue === 'all' ? [] : [Number(statusValue) as EClassRoomDisciplinesStatus],
      dateStart,
      dateEnd,
      teacherName,
      page: 1,
    });
  };

  const handleClear = () => {
    setCompanyId(null);
    setCompanyLabel('');
    setCourseId(null);
    setCourseLabel('');
    setClassStudentId(null);
    setClassStudentLabel('');
    setClassModuleId(null);
    setClassModuleLabel('');
    setTeacherId(null);
    setTeacherName('');
    setStatusValue('all');
    setDateStart('');
    setDateEnd('');
    setResetNonce(n => n + 1);
    resetFilters();
  };

  return (
    <div className="flex flex-col gap-xxs">
      <div className="grid grid-cols-1 gap-xxxs md:grid-cols-5">
        <CompanyAutocomplete
          key={`company-${resetNonce}`}
          label={t('list.filters.company')}
          placeholder={t('list.filters.companyPlaceholder')}
          value={companyId}
          selectedLabel={companyLabel}
          onChange={(id, label) => {
            setCompanyId(id);
            setCompanyLabel(label);
          }}
        />

        <CourseAutocomplete
          key={`course-${resetNonce}`}
          label={t('list.filters.course')}
          placeholder={t('list.filters.coursePlaceholder')}
          value={courseId}
          selectedLabel={courseLabel}
          onChange={handleCourseChange}
        />

        <ClassStudentAutocomplete
          key={`classStudent-${resetNonce}`}
          label={t('list.filters.classStudent')}
          placeholder={t('list.filters.classStudentPlaceholder')}
          value={classStudentId}
          selectedLabel={classStudentLabel}
          onChange={(id, label) => {
            setClassStudentId(id);
            setClassStudentLabel(label);
          }}
        />

        <ClassModuleAutocomplete
          key={`classModule-${resetNonce}-${courseId ?? 'none'}`}
          label={t('list.filters.classModule')}
          placeholder={t('list.filters.classModulePlaceholder')}
          value={classModuleId}
          selectedLabel={classModuleLabel}
          courseId={courseId}
          onChange={(id, label) => {
            setClassModuleId(id);
            setClassModuleLabel(label);
          }}
        />

        <SelectInput
          label={t('list.filters.status')}
          value={statusValue}
          onValueChange={setStatusValue}
          size="md"
        >
          <SelectInputItem custom={{ root: 'text-nowrap' }} value="all">
            {t('list.filters.statusAll')}
          </SelectInputItem>
          {CLASS_PLANNING_STATUS_VALUES.map(value => (
            <SelectInputItem custom={{ root: 'text-nowrap' }} key={value} value={String(value)}>
              {t(CLASS_PLANNING_STATUS_LABEL_KEY[value] as 'status.planned')}
            </SelectInputItem>
          ))}
        </SelectInput>
      </div>

      <div className="flex flex-col gap-xxxs md:flex-row md:items-end">
        <div className="grid flex-1 grid-cols-1 gap-xxxs md:grid-cols-3">
          <DatePicker
            key={`dateStart-${resetNonce}`}
            label={t('list.filters.dateStart')}
            value={dateStart}
            onChange={setDateStart}
          />
          <DatePicker
            key={`dateEnd-${resetNonce}`}
            label={t('list.filters.dateEnd')}
            value={dateEnd}
            onChange={setDateEnd}
          />
          <TeacherAutocomplete
            key={`teacher-${resetNonce}`}
            label={t('list.filters.teacher')}
            placeholder={t('list.filters.teacherPlaceholder')}
            value={teacherId}
            selectedLabel={teacherName}
            onChange={(id, label) => {
              setTeacherId(id);
              setTeacherName(label);
            }}
          />
        </div>

        <div className="flex items-center gap-nano">
          <Button type="button" hierarchy="tertiary" size="md" onClick={handleClear}>
            {t('list.filters.clear')}
          </Button>
          <Button
            type="button"
            hierarchy="primary"
            size="md"
            leadingIcon={FunnelIcon}
            onClick={handleSearch}
          >
            {t('list.filters.search')}
          </Button>
        </div>
      </div>
    </div>
  );
}
